import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ClipboardList, ClipboardPaste } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import FillFromNotesDialog from '../components/work/FillFromNotesDialog';
import { OwnerPicker } from '../components/work/InitiativeOwners';
import { PageFrame } from '../components/work/page-chrome';
import { useMembers } from '../components/wiki/use-member-names';
import { PageTitle } from '../components/work/page-states';
import { useOverlays } from '../components/work/overlay-context';
import { apiFetch, v1Path } from '../lib/api';
import { splitAnswers, type NotesAnswer } from '../lib/notes-fill';
import {
  fetchServiceRequestWorkspace,
  listServiceRequestTypes,
  serviceRequestPath,
  type ServiceRequest,
  type ServiceRequestType,
} from '../lib/service-requests';
import { createRelation } from '../lib/relations';
import { plainTextToHtml } from '../lib/rich-text';
import { SR_PANEL } from '../lib/service-request-views';
import { cn } from '../lib/utils';

type FormValues = Record<string, string | boolean | number>;
type SalesOpsSource = { kind: 'sales_ops'; key: string; title: string; workItemId: string; projectId: string };
type NewServiceRequestState = { title?: unknown; source?: unknown };

/** "Device Deployment" → "Device deployment"; leaves acronyms like "POS" alone. */
function sentenceCase(name: string): string {
  return name.split(' ').map((word, index) => (index > 0 && /^[A-Z][a-z]/.test(word) ? word.toLowerCase() : word)).join(' ');
}

const TITLE_EXAMPLES: Record<string, string> = {
  'device-deployment': 'e.g. Deploy 10 devices at UCL',
  'event-booking': 'e.g. HIMSS demo, 2 machines, 12–14 Mar',
};

/** An example title that fits the chosen request type. */
export function requestTitleExample(type: Pick<ServiceRequestType, 'key' | 'name'>): string {
  return TITLE_EXAMPLES[type.key] ?? `e.g. ${sentenceCase(type.name)} for Acme Ltd`;
}

export default function NewServiceRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useOverlays();
  const locationState = location.state as NewServiceRequestState | null;
  const source = useMemo<SalesOpsSource | null>(() => {
    const raw = locationState?.source;
    if (!raw || typeof raw !== 'object') return null;
    const value = raw as Record<string, unknown>;
    return value.kind === 'sales_ops'
      && typeof value.key === 'string'
      && typeof value.title === 'string'
      && typeof value.workItemId === 'string'
      && typeof value.projectId === 'string'
      ? value as SalesOpsSource
      : null;
  }, [locationState?.source]);
  const [types, setTypes] = useState<ServiceRequestType[]>([]);
  const [projectId, setProjectId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [title, setTitle] = useState(() => {
    if (typeof locationState?.title === 'string') return locationState.title;
    return source?.title ?? '';
  });
  const [plainDescription, setPlainDescription] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const people = useMembers();
  const [details, setDetails] = useState<FormValues>(() => {
    const initial: FormValues = {};
    if (source) initial.project_reference = source.key;
    return initial;
  });
  const [typeData, setTypeData] = useState<FormValues>({});
  const [notesOpen, setNotesOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const selectedType = useMemo(() => types.find((row) => row.id === typeId) ?? null, [typeId, types]);

  useEffect(() => setTypeData({}), [typeId]);

  const answered = useMemo(
    () => new Set([
      ...Object.keys(details).filter((field) => details[field] !== '').map((field) => `detail:${field}`),
      ...Object.keys(typeData),
    ]),
    [details, typeData],
  );

  function fillFromNotes(answers: NotesAnswer[]) {
    const found = splitAnswers(answers);
    setDetails((current) => ({ ...found.details, ...Object.fromEntries(Object.entries(current).filter(([, value]) => value !== '')) }));
    setTypeData((current) => ({ ...found.typeData, ...current }));
    if (!title.trim() && found.details.customer && selectedType) setTitle(`${sentenceCase(selectedType.name)} for ${found.details.customer}`);
    toast(`Filled ${answers.length} ${answers.length === 1 ? 'answer' : 'answers'} from your notes`);
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([listServiceRequestTypes(), fetchServiceRequestWorkspace()])
      .then(([catalog, workspace]) => {
        if (cancelled) return;
        setTypes(catalog);
        if (source) {
          setTypeId((current) => current || catalog.find((type) => type.key === 'device-deployment')?.id || '');
        }
        setProjectId(workspace.project.id);
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Failed to load Service Request form'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [source, toast]);

  /** Saves whatever is filled in so far; the request stays a Draft until submitted. */
  async function create() {
    if (!projectId || !typeId || !selectedType) return;
    const draftTitle = title.trim() || `${selectedType.name} draft`;
    setBusy(true);
    try {
      const common = Object.fromEntries(
        Object.entries(details).filter(([, value]) => value !== ''),
      );
      const result = await apiFetch<ServiceRequest>(v1Path(`/projects/${projectId}/service-requests`), {
        method: 'POST',
        body: JSON.stringify({
          request_type_id: typeId,
          title: draftTitle,
          description: plainTextToHtml(plainDescription),
          priority: 'medium',
          ...(assigneeIds.length ? { assignee_ids: assigneeIds } : {}),
          details: { ...common, type_data: typeData },
        }),
      });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      if (source) {
        try {
          await createRelation(result.work_item.project_id, result.work_item.id, 'relates_to', source.key);
          toast(`Draft ${result.work_item.key} saved and linked to ${source.key}`);
        } catch (err) {
          toast(`Draft ${result.work_item.key} saved, but the ${source.key} link failed: ${err instanceof Error ? err.message : 'unknown error'}`);
        }
      }
      navigate(serviceRequestPath(result.work_item));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to create Service Request');
    } finally {
      setBusy(false);
    }
  }

  const canSave = Boolean(projectId && typeId) && !busy;
  const shownOnForm = new Set(['target_go_live_date', 'project_reference']);
  const notesCount = Object.keys(typeData).length
    + Object.keys(details).filter((field) => !shownOnForm.has(field) && details[field] !== '').length;

  return (
    <PageFrame>
        <div className="mx-auto max-w-6xl space-y-5">
          <Link to="/service-requests" className="inline-flex items-center gap-1.5 text-sm text-primary hover:no-underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Service Requests
          </Link>
          <div className="space-y-3">
            <PageTitle title="New service request" />
            {selectedType ? (
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-muted-foreground">Request type</span>
                {source ? (
                  <span className="font-medium">{sentenceCase(selectedType.name)}</span>
                ) : (
                  <>
                    <Select value={typeId} onValueChange={setTypeId}>
                      <SelectTrigger aria-label="Request type" className="h-8 w-auto gap-2 border-0 bg-transparent px-1 font-medium shadow-none focus:ring-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {types.map((type) => <SelectItem key={type.id} value={type.id}>{sentenceCase(type.name)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="h-5 w-px bg-border" aria-hidden />
                    <button type="button" className="font-medium text-primary underline-offset-2 hover:underline" onClick={() => setTypeId('')}>Change type</button>
                  </>
                )}
              </div>
            ) : null}
          </div>
          {source ? (
            <p className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              Creating a Device Deployment draft from <span className="font-mono font-medium">{source.key}</span>. The draft will be linked back to the Sales Ops item.
            </p>
          ) : null}
          {!loading && selectedType ? (
            <form
              className={cn(SR_PANEL, 'mx-auto grid max-w-2xl gap-4 p-6')}
              onSubmit={(event) => {
                event.preventDefault();
                if (canSave && title.trim()) void create();
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1.5">
                  <h2 className="text-lg font-semibold leading-none">{sentenceCase(selectedType.name)}</h2>
                  <p className="text-sm text-muted-foreground">Create a draft and assign the people who will work on it. The rest of the answers can wait, and the project work can be set up from the draft.</p>
                </div>
                <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => setNotesOpen(true)}>
                  <ClipboardPaste className="mr-1.5 h-4 w-4" aria-hidden />
                  Fill from notes
                </Button>
              </div>
              <div className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="sr-title">Request title</Label>
                  <Input
                    id="sr-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder={requestTitleExample(selectedType)}
                    required
                    autoFocus
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="sr-target">Requested completion date</Label>
                    <Input
                      id="sr-target"
                      type="date"
                      aria-describedby="sr-target-hint"
                      value={typeof details.target_go_live_date === 'string' ? details.target_go_live_date : ''}
                      onChange={(event) => setDetails((current) => ({ ...current, target_go_live_date: event.target.value }))}
                    />
                    <p id="sr-target-hint" className="text-xs text-muted-foreground">When do you need this completed?</p>
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label id="sr-assignees-label">Assign to</Label>
                  <OwnerPicker
                    label="request"
                    value={assigneeIds}
                    members={Array.from(people.values()).filter((member) => member.is_active)}
                    onChange={setAssigneeIds}
                  />
                  <p className="text-xs text-muted-foreground">They are assigned as soon as the draft is created.</p>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="sr-description">Request summary</Label>
                  <Textarea
                    id="sr-description"
                    value={plainDescription}
                    onChange={(event) => setPlainDescription(event.target.value)}
                    rows={3}
                    placeholder="Briefly describe what you need, where, and why."
                  />
                </div>
              </div>
              {notesCount ? (
                <p className="rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
                  {notesCount} more {notesCount === 1 ? 'answer' : 'answers'} from your notes will be filled in on the draft. You can check and change them there.
                </p>
              ) : null}
              <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" disabled={busy} onClick={() => (source ? navigate('/service-requests') : setTypeId(''))}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSave || title.trim().length === 0}>
                  {busy ? 'Creating…' : 'Create draft'}
                </Button>
              </div>
            </form>
          ) : null}
          {selectedType && notesOpen ? (
            <FillFromNotesDialog open onOpenChange={setNotesOpen} type={selectedType} answered={answered} onApply={fillFromNotes} />
          ) : null}

          {loading ? <p className="text-sm text-muted-foreground">Loading request types…</p> : null}

          {!loading && !selectedType ? (
            types.length ? (
              <div className="space-y-3">
                <h2 className="text-base font-semibold">What do you need?</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {types.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setTypeId(type.id)}
                      className={cn(SR_PANEL, 'group flex h-full flex-col gap-3 p-5 text-left transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md')}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <ClipboardList className="h-5 w-5" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-base font-semibold">{type.name}</span>
                        {type.description ? <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">{type.description}</span> : null}
                      </span>
                      <span className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{[type.department, type.sla_days ? `${type.sla_days} day SLA` : null].filter(Boolean).join(' · ')}</span>
                        <ArrowRight className="h-4 w-4 text-primary transition group-hover:translate-x-0.5" aria-hidden />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={cn(SR_PANEL, 'flex flex-col items-center gap-2 px-6 py-16 text-center')}>
                <p className="text-base font-semibold">No request types are live yet</p>
                <p className="text-sm text-muted-foreground">An administrator can create one in Service Request configuration.</p>
              </div>
            )
          ) : null}
        </div>
    </PageFrame>
  );
}
